import { useState, useEffect } from 'react'
import { useEvidenceStore } from '../store/evidenceStore'
import { useAuthStore } from '../store/authStore'
import { 
  RefreshCw, 
  Database, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  ExternalLink,
  Settings
} from 'lucide-react'

const FormStructureManager = () => {
  const [structureInfo, setStructureInfo] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  const { 
    formStructure, 
    fetchFormStructure, 
    refreshFormStructure,
    isLoadingStructure 
  } = useEvidenceStore()
  const { token, user } = useAuthStore()

  useEffect(() => {
    loadStructureInfo()
  }, [])

  const loadStructureInfo = async () => {
    try {
      const params = new URLSearchParams()
      if (user?.email) params.append('email', user.email)
      
      const url = `/api/form-structure${params.toString() ? '?' + params.toString() : ''}`
      const response = await fetch(url)
      const data = await response.json()
      setStructureInfo(data.meta)
    } catch (error) {
      console.error('Error obteniendo info de estructura:', error)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      if (user?.rol === 'admin') {
        await refreshFormStructure(token)
      } else {
        await fetchFormStructure(true, user?.email)
      }
      await loadStructureInfo()
    } finally {
      setIsRefreshing(false)
    }
  }

  const formatCacheAge = (seconds) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Settings className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Gestión de Estructura del Formulario
          </h3>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || isLoadingStructure}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${(isRefreshing || isLoadingStructure) ? 'animate-spin' : ''}`} />
          <span>{user?.rol === 'admin' ? 'Actualizar Cache' : 'Recargar'}</span>
        </button>
      </div>

      {/* Estado del cache */}
      {structureInfo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Database className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Estado del Cache</span>
            </div>
            <div className="flex items-center space-x-2">
              {structureInfo.cached ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              )}
              <span className="text-sm text-gray-900">
                {structureInfo.cached ? 'En Cache' : 'Recién Actualizado'}
              </span>
            </div>
            {structureInfo.cacheAge && (
              <p className="text-xs text-gray-600 mt-1">
                Edad: {formatCacheAge(structureInfo.cacheAge)}
              </p>
            )}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Última Actualización</span>
            </div>
            <p className="text-sm text-gray-900">
              {structureInfo.lastUpdated 
                ? new Date(structureInfo.lastUpdated).toLocaleString('es-ES')
                : 'No disponible'
              }
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Database className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Contenido</span>
            </div>
            <p className="text-sm text-gray-900">
              {structureInfo.totalDimensions} dimensiones
            </p>
            <p className="text-sm text-gray-900">
              {structureInfo.totalCriterios} criterios
            </p>
            {structureInfo.userLevel && (
              <p className="text-xs text-blue-600 mt-1">
                Filtrado para: {structureInfo.userLevel}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Estructura actual */}
      {formStructure && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Estructura Actual:</h4>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {Object.entries(formStructure).map(([dimension, criterios]) => (
              <div key={dimension} className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">{dimension}</h5>
                <div className="space-y-2">
                  {criterios.map((criterio, index) => (
                    <div key={index} className="pl-4 border-l-2 border-gray-200">
                      <p className="text-sm font-medium text-gray-800">
                        {typeof criterio === 'object' ? criterio.nombre : criterio}
                      </p>
                      {typeof criterio === 'object' && criterio.descripcion && (
                        <p className="text-xs text-gray-600 mt-1">
                          {criterio.descripcion}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instrucciones para administradores */}
      {user?.rol === 'admin' && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">
            Instrucciones para Administradores
          </h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              • Para modificar la estructura, edita la pestaña <strong>"form_structure"</strong> en Google Sheets
            </p>
            <p>
              • Usa las columnas: <code>dimension</code>, <code>criterio</code>, <code>descripcion</code>, <code>niveles_aplicables</code>
            </p>
            <p>
              • La columna <code>niveles_aplicables</code> puede ser: <strong>pregrado</strong>, <strong>posgrado</strong> o <strong>ambos</strong>
            </p>
            <p>
              • Los criterios se filtran automáticamente según el nivel del programa del usuario
            </p>
            <p>
              • Después de hacer cambios, haz clic en "Actualizar Cache" para aplicarlos
            </p>
          </div>
          
          <div className="mt-3 space-y-2">
            <a
              href={`https://docs.google.com/spreadsheets/d/${process.env.GOOGLE_SHEET_ID || 'tu-sheet-id'}/edit#gid=0`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Abrir Google Sheets</span>
            </a>
            
            <div className="text-xs text-blue-700 mt-2">
              <strong>Niveles de programa:</strong>
              <ul className="list-disc list-inside ml-2 mt-1">
                <li><code>pregrado</code>: Solo para programas de pregrado</li>
                <li><code>posgrado</code>: Solo para programas de posgrado (magíster, doctorado)</li>
                <li><code>ambos</code>: Para todos los niveles (por defecto)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Errores */}
      {structureInfo?.error && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Advertencia</span>
          </div>
          <p className="text-sm text-yellow-700 mt-1">{structureInfo.error}</p>
        </div>
      )}
    </div>
  )
}

export default FormStructureManager